<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCommentsList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parentId: {
      type: String,
      required: false,
    },
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });

  const $data = computed(() => ({
    container: container.value,
    ux: ux.value,
  }));

  const $$load_container = () => {
    return $fn.value.fetch(`list_comment`, {
      parameters: { 'object-id': props.parentId || props.parent.id },
      asResourceContainer: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'container' }, { name: 'ux' }],
    loaders: { container: $$load_container },
    ux: { initialLoadSpinner: true },
    dataRefs: { container, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    if (loaded.value) {
      return container.value.data;
    }
    return null;
  });

  watch(
    () => props.parentId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parentId');
    }
  );
  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_showModal_ub5k55t4m_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WComment',
      props: {
        parentId: props.parentId,
        parent: props.parent,
        container: container.value,
      },
    };
  };
  const act_294572930540096_showModal_0q1sy3qad_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WComment',
      props: {
        parent: props.parent,
        container: container.value,
        item: $event.scope.item,
      },
    };
  };
  const act_3365375699802249_rest_qy6oqmtt8_getActionArgs = async ($event) => {
    return {
      method: 'delete',
      container: container.value,
      resource: $event.scope.item,
    };
  };
  const act_3365375699802249_rest_qy6oqmtt8_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_foqm8lzwh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_crds7dj0i =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_foqm8lzwh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_crds7dj0i],
    event: 'reload',
  });
  const act_2095089031469157_showModal_ub5k55t4m =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_ub5k55t4m_getActionArgs,
    });
  const inst_evh_2095089031469157_clickPreventStop_e2uqqv5ja =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_ub5k55t4m,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_e2uqqv5ja],
      event: 'click.prevent.stop',
    });
  const act_294572930540096_showModal_0q1sy3qad =
    new actionTypes.ShowModalAction({
      actionArgs: act_294572930540096_showModal_0q1sy3qad_getActionArgs,
    });
  const inst_evh_294572930540096_clickPreventStop_jirdhmid6 =
    new actionTypes.EventHandlerImpl({
      action: act_294572930540096_showModal_0q1sy3qad,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_294572930540096_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_294572930540096_clickPreventStop_jirdhmid6],
      event: 'click.prevent.stop',
    });
  const act_3365375699802249_rest_qy6oqmtt8 = new actionTypes.RestAction({
    actionArgs: act_3365375699802249_rest_qy6oqmtt8_getActionArgs,
    confirm: act_3365375699802249_rest_qy6oqmtt8_getConfirm,
  });
  const inst_evh_3365375699802249_clickPreventStop_b8tyi63fa =
    new actionTypes.EventHandlerImpl({
      action: act_3365375699802249_rest_qy6oqmtt8,
      event: 'click.prevent.stop',
      displayName: 'rest',
    });
  const inst_evh_3365375699802249_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3365375699802249_clickPreventStop_b8tyi63fa],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_crds7dj0i,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_clickPreventStop_e2uqqv5ja,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_294572930540096_clickPreventStop_jirdhmid6,
    inst_evh_294572930540096_clickPreventStop,
    inst_evh_3365375699802249_clickPreventStop_b8tyi63fa,
    inst_evh_3365375699802249_clickPreventStop,
  };

  const evh_7315092382398562_reload_crds7dj0i = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_crds7dj0i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_e2uqqv5ja = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_e2uqqv5ja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop_jirdhmid6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_294572930540096_clickPreventStop_jirdhmid6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_294572930540096_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_294572930540096_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop_b8tyi63fa = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop_b8tyi63fa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3365375699802249_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3365375699802249_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    loading-spinner
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ gettext('Comments') }}
          </strong>
          <ItpButton
            :text="gettext('Add comment...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No comments have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
        >
          <ItpBox spacing="pb-1">
            <p>
              <ItpText :text="item.message" pre> </ItpText>
            </p>
            <BDropdown
              size="sm"
              toggle-class="btn-default-borderless"
              no-caret
              variant="none"
              class="ms-auto mb-auto"
              v-if="!item.locked"
            >
              <template #button-content>
                <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
              </template>
              <BDropdownItem
                @click.prevent.stop="
                  evh_294572930540096_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
              </BDropdownItem>
              <BDropdownItem
                v-if="!item.locked"
                @click.prevent.stop="
                  evh_3365375699802249_clickPreventStop($event, { item })
                "
              >
                <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
              </BDropdownItem>
            </BDropdown>
          </ItpBox>
          <div class="d-flex align-items-end">
            <ItpText muted small>
              {{ datetime(item.created_at) }}
            </ItpText>
            <small class="text-muted text-nowrap mx-2">
              {{ username(item.updated_by || item.created_by) }}
            </small>
            <div
              class="ms-2 d-flex align-self-end flex-grow-1 justify-content-end"
            >
              <ItpIcon
                :title="gettext('Locked')"
                name="fa-lock"
                class="text-muted"
                v-if="item.locked"
              >
              </ItpIcon>
            </div>
          </div>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
